"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileSearch, FileSpreadsheet, Award, ArrowRight, ShieldCheck, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const stages = [{
  stepNumber: "01",
  stageKey: "revision",
  title: "REVISIÓN DE BASES Y JUNTA DE ACLARACIONES",
  shortTitle: "Diagnóstico y aclaraciones",
  icon: FileSearch,
  description: "Auditoría técnica de convocatorias, bases y anexos para identificar requisitos de cumplimiento y formular preguntas oportunas en las juntas públicas.",
  deliverables: ["Auditoría de requisitos legales y técnicos", "Detección de condiciones restrictivas", "Redacción y presentación de preguntas"]
}, {
  stepNumber: "02",
  stageKey: "preparacion",
  title: "PREPARACIÓN Y SOLVENTACIÓN DE LA PROPUESTA",
  shortTitle: "Integración de expedientes",
  icon: FileSpreadsheet,
  description: "Armado riguroso de sobres técnicos, administrativos y económicos, asegurando que cada documento cumpla las formalidades normativas solicitadas.",
  deliverables: ["Estructuración de expedientes por sobre", "Validación de constancias y vigencias", "Cálculo y revisión de propuestas económicas"]
}, {
  stepNumber: "03",
  stageKey: "seguimiento",
  title: "SEGUIMIENTO DEL PROCEDIMIENTO Y FALLO",
  shortTitle: "Representación y cierre",
  icon: Award,
  description: "Acompañamiento en los actos de apertura, evaluación del dictamen institucional, asistencia técnica ante el fallo y revisión del contrato definitivo.",
  deliverables: ["Presencia en actos de apertura de propuestas", "Análisis técnico del dictamen de evaluación", "Acompañamiento en la formalización de contrato"]
}];
export default function Methodology() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="cómo-te-acompañamos" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="container mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        {/* Header Block */}
        <div className="max-w-3xl mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-muted/60 border border-border text-xs font-serif font-bold uppercase tracking-widest text-foreground mb-4">
            <ShieldCheck className="size-3.5 text-foreground" />
            <span>Metodología de contratación</span>
          </div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-semibold text-foreground uppercase tracking-wider mb-4 leading-tight">
            Cómo te acompañamos
          </h2>

          {/* Signature Hairline Accent */}
          <div className="w-12 h-[2px] bg-foreground/70 mb-6" />

          <p className="text-base md:text-lg font-sans text-muted-foreground leading-relaxed">
            Te representamos en cada etapa del proceso, desde las bases hasta la firma del contrato, para que compitas con expedientes solventes y respaldo legal.
          </p>
        </div>

        {/* Methodology 3-Stage Track */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {stages.map((stage, i) => {
          const Icon = stage.icon;
          return <motion.div key={stage.stageKey} variants={itemVariants} className="flex" data-index={i}>
                <Card className="w-full flex flex-col justify-between bg-card text-card-foreground border border-border rounded-sm shadow-xs transition-[box-shadow,border-color] duration-300 hover:shadow-md hover:border-foreground/40 py-8 px-6 md:px-8 relative">
                  {/* Top hairline accent rule */}
                  <div className="absolute top-0 left-0 right-0 h-[2px] bg-foreground/20" />

                  <div>
                    {/* Stage Header */}
                    <div className="flex items-center justify-between gap-4 mb-6">
                      <div className="flex items-center justify-center size-11 rounded-sm bg-muted border border-border text-foreground">
                        <Icon className="size-5" />
                      </div>
                      <span className="text-2xl font-serif font-bold text-muted-foreground/60 tracking-wider">
                        {stage.stepNumber}
                      </span>
                    </div>

                    <CardHeader className="p-0 mb-4">
                      <CardTitle className="text-base font-serif font-semibold text-foreground uppercase tracking-wide leading-snug">
                        {stage.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="p-0">
                      <p className="text-sm font-sans text-muted-foreground leading-relaxed mb-6">
                        {stage.description}
                      </p>

                      <Separator className="my-5 bg-border" />

                      <div className="space-y-2.5">
                        <span className="text-xs font-serif uppercase tracking-wider font-semibold text-foreground block">
                          Actividades clave
                        </span>
                        <ul className="space-y-2 text-xs font-sans text-muted-foreground">
                          {stage.deliverables.map((item, dIndex) => <li key={`${stage.stageKey}-${dIndex}`} data-index={dIndex} className="flex items-start gap-2 leading-relaxed">
                              <CheckCircle2 className="size-3.5 text-foreground shrink-0 mt-0.5" />
                              <span>{item}</span>
                            </li>)}
                        </ul>
                      </div>
                    </CardContent>
                  </div>

                  <div className="pt-8 mt-6 border-t border-border">
                    <span className="text-xs font-sans text-muted-foreground">
                      Etapa {stage.stepNumber} · Acompañamiento especializado
                    </span>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Institutional Bottom Banner / Direct Action */}
        <div className="mt-14 lg:mt-18 pt-10 border-t border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-6 bg-card/60 p-6 md:p-8 rounded-sm border">
          <div className="max-w-2xl">
            <h3 className="text-lg md:text-xl font-serif font-semibold uppercase tracking-wide text-foreground mb-1">
              ¿Tienes un procedimiento en curso?
            </h3>
            <p className="text-sm font-sans text-muted-foreground">
              Revisamos los términos de referencia y bases de licitación para evaluar la viabilidad de tu propuesta.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button asChild className="rounded-sm bg-primary text-primary-foreground font-serif uppercase text-xs tracking-wider font-semibold px-6 py-5 border border-border shadow-xs hover:bg-primary/90 transition-colors">
              <Link to="/ContactPage">
                <span>Solicitar asesoría</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}